//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';

import { CuralateCatalogProduct } from '@apis/universal-image-actions/universalImageActionsApiModel';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { BodyText } from '@components/atoms/text/Text';
import SearchBar from '@components/molecules/search-bar/SearchBar';
import Cuid from '@models/Cuid';
import { Product } from '@models/IItemMetadata';
import {
  getIsEcommerceIntegrationsMagentoEnabled,
  getIsEcommerceIntegrationsShopifyEnabled,
} from '@redux/selectors/ecommerceIntegrationsSelectors';
import { NoCatalog } from '@uia/products/NoCatalog';
import { UiaCatalogClientSelector } from '@uia/products/UiaCatalogClientSelector';
import { UiaProductGrid } from '@uia/products/UiaProductGrid';
import { UiaProductTrackingIds } from '@uia/products/constants/UiaProductConstants';
import { useProductTaggingContext } from '@uia/products/context/ProductTaggingContext';
import { ProductEditor } from '@uia/products/editor/ProductEditor';
import { useCurrentTrackingParamsQuery } from '@uia/products/hooks/useCurrentTrackingParamsQuery';
import { useProductCatalogClientsQuery } from '@uia/products/hooks/useProductCatalogClientsQuery';
import { getIsUrlTrackingParamsEnabled } from '@uia/products/selectors/getIsUrlTrackingParamsEnabled';
import { isDefined, isNotNull, isUndefined } from '@utils/filterPredicates';

export interface UiaTagProductsProps {
  enableEditor: (product: Product | CuralateCatalogProduct) => void;
  onClose: () => void;
  selectedProducts: CuralateCatalogProduct[];
  setSelectedProducts: (selectedProducts: CuralateCatalogProduct[]) => void;
}

export const UiaTagProducts = ({
  enableEditor,

  selectedProducts,
  setSelectedProducts,
}: UiaTagProductsProps) => {
  const {
    addProduct,
    catalogClientId,
    cuid,
    hasCatalogConnected,
    metadata,
    searchQuery,
    selectedProductToEdit,
    setCatalogClientId,
    setIsTaggingProducts,
    setOffset,
    setSearchQuery,
    shouldShowCatalogSelector,
  } = useProductTaggingContext();

  const isTrackingParamsEnabled = useSelector(getIsUrlTrackingParamsEnabled);
  const isShopifyEnabled = useSelector(
    getIsEcommerceIntegrationsShopifyEnabled,
  );
  const isMagentoEnabled = useSelector(
    getIsEcommerceIntegrationsMagentoEnabled,
  );

  const { data: trackingParamsVersion, isPending: isLoadingTrackingParams } =
    useCurrentTrackingParamsQuery();

  const { data: catalogClients, isPending: isLoadingCatalogClients } =
    useProductCatalogClientsQuery(
      Cuid.toPartial(cuid),
      shouldShowCatalogSelector,
    );

  // Set the first client id as the default value.
  if (
    isUndefined(catalogClientId) &&
    isDefined(catalogClients?.[0]?.clientId)
  ) {
    setCatalogClientId(catalogClients[0].clientId);
  }

  const tagSingleProduct = (product: CuralateCatalogProduct) => {
    if (isUndefined(product.destinationUrl)) {
      return;
    }

    const productImageProperties = product.images[0] ?? {};

    const trackingSettings = {
      trackingParamsVersion:
        isTrackingParamsEnabled &&
        isDefined(trackingParamsVersion) &&
        isNotNull(trackingParamsVersion)
          ? trackingParamsVersion
          : undefined,
    };

    addProduct(
      {
        ...product,
        ...productImageProperties,
        ...trackingSettings,
        position: metadata.productTags.length + 1,
      },
      metadata.productTags,
    );
    setIsTaggingProducts(false);
    setSearchQuery('');
  };

  if (
    isLoadingTrackingParams ||
    (shouldShowCatalogSelector && isLoadingCatalogClients)
  ) {
    return <CenteredSpinner />;
  }

  return (
    <>
      {/* Only show catalog client selector when theres more than 1 catalog client to choose from */}
      {shouldShowCatalogSelector &&
        isDefined(catalogClients) &&
        catalogClients.length > 1 && (
          <UiaCatalogClientSelector
            catalogClientId={catalogClientId}
            catalogClients={catalogClients}
            setCatalogClientId={setCatalogClientId}
          />
        )}
      {isUndefined(selectedProductToEdit) && (
        <>
          <BodyText>Search Product by Name, URL, or SKU</BodyText>
          <SearchBar
            onChange={(search) => {
              setOffset(0);
              setSearchQuery(search);
            }}
            placeholder='Product name, SKU, or URL'
            trackingIdPrefix={UiaProductTrackingIds.PRODUCT_SEARCH_BAR}
            value={searchQuery}
            visuallyHiddenLabel='Product Search'
          />
        </>
      )}

      <UiaProductGrid
        key={searchQuery}
        enableEditor={enableEditor}
        selectedProducts={selectedProducts}
        setSelectedProducts={setSelectedProducts}
        tagSingleProduct={tagSingleProduct}
      />

      {!hasCatalogConnected && isUndefined(selectedProductToEdit) && (
        <NoCatalog
          isEcommerceIntegrationEnabled={isShopifyEnabled || isMagentoEnabled}
        />
      )}
      {isDefined(selectedProductToEdit) && <ProductEditor />}
    </>
  );
};
